import { KeyboardArrowLeft, Menu } from '@mui/icons-material';
import { Box, Chip, IconButton, ListItemIcon, ListItemText, MenuItem, Typography } from '@mui/material';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { menu } from '../constants/menu';
import { boxShadow } from '../constants/styles';
import { t } from '../constants/translations';
import { useAdmin } from '../contexts/admin.context';
import { useCafe } from '../contexts/cafe.context';
import { useLocale } from '../contexts/locale.context';
import useMedia from '../utils/useMedia';
import Header from './Header/Header';
import Logo from './Logo';

const AdminLayout = ({ children }: PropsWithChildren) => {
  const { isMobile } = useMedia();
  const { cafeSlug, isPlus, isPro, online, cafe } = useAdmin();
  const { lang } = useLocale();
  const { pathname } = useLocation();
  const { worker } = useCafe();
  const navigate = useNavigate();
  const [fixed, setFixed] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);
  const isPOS = useMemo(
    () => pathname.includes('/pos') || pathname.includes('kitchen') || pathname.includes('editTableMap'),
    [pathname]
  );
  const isBurger = isMobile || isPOS;
  const isWide = isMobile || fixed || hovered || isPOS;
  const bodyPadding = fixed ? '312px' : '88px';
  const menuWidth = isWide ? 280 : 56;
  const mobileMenuWidth = open ? 280 : 0;

  const goTo = useCallback(
    (path: string = '') => {
      setOpen(false);
      navigate(`/${cafeSlug}/${path}`);
    },
    [cafeSlug, navigate]
  );

  return (
    <>
      {isBurger && (
        <Box
          onClick={() => setOpen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: (t) => `${t.palette.grey[900]}22`,
            opacity: open ? 1 : 0,
            zIndex: 100,
            pointerEvents: open ? 'auto' : 'none',
            backdropFilter: open ? 'blur(4px)' : undefined,
            transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          }}
        />
      )}

      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: 'fixed',
          width: isBurger ? mobileMenuWidth : menuWidth,
          py: 2,
          px: isBurger && !open ? 0 : 2,
          top: 0,
          bottom: 0,
          left: 0,
          boxShadow,
          bgcolor: (t) => t.palette.background.paper + (isBurger ? '' : 'bb'),
          backdropFilter: isBurger ? undefined : 'blur(6px)',
          transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, padding 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          overflow: 'auto',
          zIndex: isBurger ? 2000 : 9,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box sx={{ mb: 2, ml: 2 }}>
            <Logo
              sx={{
                width: 100,
                opacity: isWide ? 1 : 0,
                transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
              }}
            />
          </Box>

          {!isBurger && (
            <IconButton
              onClick={() => setFixed(!fixed)}
              sx={{
                opacity: isWide ? 1 : 0,
                transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
              }}
            >
              <KeyboardArrowLeft
                sx={{
                  transform: `rotate(${fixed ? '0' : '180'}deg)`,
                  transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                }}
              />
            </IconButton>
          )}
        </Box>

        {worker &&
          menu.map(({ group, items }, index) => (
            <Box key={group || `empty`}>
              {group && items.some((i) => !i.credentialKey || worker.credentials[i.credentialKey]) && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    mt: 2,
                    mb: 1,
                    mx: 2,
                    opacity: isWide ? 1 : 0,
                    transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    color: (t) => t.palette.text.secondary,
                  }}
                >
                  {t[group][lang].toLocaleUpperCase()}
                </Typography>
              )}

              {items.map((item) => {
                const isActive = pathname.split('/')[2] === item.route;

                if (item.credentialKey && !worker.credentials[item.credentialKey]) {
                  return null;
                }

                if (item.name === 'self_order_points' && cafe?.type !== 'selfOrderPoins') {
                  return null;
                }

                return (
                  <MenuItem
                    onClick={() => goTo(item.route)}
                    key={item.name}
                    sx={{
                      borderRadius: 1,
                      mb: '4px',
                      height: 48,
                      bgcolor: (t) => (isActive ? `${t.palette.primary.main}11` : 'none'),
                      color: (t) => (isActive ? t.palette.primary.main : t.palette.text.secondary),
                    }}
                  >
                    <ListItemIcon>
                      <item.icon
                        sx={{
                          color: (t) => (isActive ? t.palette.primary.main : t.palette.text.secondary),
                        }}
                      />

                      {item.badge && <item.badge />}
                    </ListItemIcon>

                    <ListItemText
                      sx={{
                        opacity: isWide ? 1 : 0,
                        transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                      }}
                    >
                      {t[item.name][lang]}
                      {item.plan === 'plus' && !isPlus && !isPro && (
                        <Chip
                          sx={{ ml: 1, height: 16, fontSize: 10 }}
                          label="PLUS"
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                      {item.plan === 'pro' && !isPro && (
                        <Chip
                          sx={{ ml: 1, height: 16, fontSize: 10 }}
                          label="PRO"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </ListItemText>
                  </MenuItem>
                );
              })}
            </Box>
          ))}

        <Box p={2} />
      </Box>

      <Box
        sx={{
          pl: isBurger ? 0 : bodyPadding,
          transition: 'padding 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: online ? undefined : (t) => `${t.palette.error.light}22`,
            mb: online ? 0 : 1,
          }}
        >
          {isBurger && (
            <IconButton size="large" sx={{ ml: 2 }} onClick={() => setOpen(true)}>
              <Menu fontSize="large" />
            </IconButton>
          )}
          <Header />
        </Box>

        <Box sx={{ maxWidth: 1400, pt: isBurger ? 0 : 2, pb: 2, px: isMobile ? 1 : 3, m: 'auto' }}>{children}</Box>
      </Box>
    </>
  );
};

export default AdminLayout;
